import React from "react"
import { useSelector } from "react-redux"
import { Redirect } from "react-router-dom"
import { getConsumerSubscription } from "#/redux/reducers/userInfo"
import { useConsumerRoutesEnabled } from "#/services/launchdarkly/useConsumerRoutesEnabled"
import {
  CONSUMER_REGISTRATION_RESUME_PATH,
  isLapsedExternalStorefrontMember,
  shouldResumeExternalRegistration
} from "#/services/consumerSubscription/externalStorefront"
import { LoadingInner } from "./Loading"
import { LapsedExternalStorefrontHandoff } from "./LapsedExternalStorefrontHandoff"

// A restored session or SAML relaunch (or an MFA sign-in, which signs in without reading the login
// response's subscription) can belong to an external-storefront member who hasn't finished registering, or
// whose subscription has lapsed. Holds the app back until this session's users/me (the bootstrap's own read)
// answers, so they never see the app, the paywall or a Stripe billing page first:
//   - registration_incomplete → the registration resume page.
//   - lapsed → their store's landing url (`landingUrl`, formerly `url`), the same hand-off login makes. Not gated on the consumer-routes
//     flag: it leaves the app entirely, so there is no unmounted route to loop through.
// A users/me failure falls through to the app, which reports it.
export function ExternalRegistrationGuard({ children }: { children: React.ReactElement }): React.ReactElement {
  const consumerRoutesEnabled = useConsumerRoutesEnabled()
  const consumerSubscription = useSelector(getConsumerSubscription)

  if (!consumerSubscription.loaded) {
    return <LoadingInner />
  }
  if (consumerRoutesEnabled && shouldResumeExternalRegistration(consumerSubscription.value)) {
    return <Redirect to={CONSUMER_REGISTRATION_RESUME_PATH} />
  }
  if (isLapsedExternalStorefrontMember(consumerSubscription.value)) {
    return <LapsedExternalStorefrontHandoff subscription={consumerSubscription.value} />
  }
  return children
}
